'use client';
import { useModel } from 'umi';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import PageMain from '@/components/page-main';
import FullScreenSpin from '@/components/FullscreenSpin';
import dayjs from 'dayjs';
import {
  Layout,
  Button,
  Dropdown,
  Input,
  Typography,
  Card,
  Radio,
  Space,
  Tag,
  Select,
  Tooltip,
  Mentions,
  Divider,
  DatePicker,
  Modal,
  Tabs,
  Checkbox,
} from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import Preview from './components/preview';
import RotateCaptchaModal from './components/captcha';
import {
  rewriteNoteTitle,
  getAtUser,
  getSuggestTags,
  getCustomizeTag,
  clearXhsCookies,
  getXhsAccountInfo,
  getNoteByUrl,
  publishImageNote,
  publishVideoNote,
  scheduleImageNote,
  activate,
  saveNote,
  getNote,
} from '@/services/xiaohongshu';
import clientSocket from '@/services/clientSocket';
import LoginModal from './components/loginModal';
import LoginSms from './components/loginSms';
import cookiesUploadModal from './components/cookiesUploadModal';
import ImageWall from './components/imageWall';
import VideoEditor from './components/videoEditor';
import ContentEditor from './components/contentEditor';
import TrailerEditor from './components/trailer';
import GoodsSelector from './components/goods';
import ProductDisplayCard from './components/goodsCard';
import { calculateUnitCharCount, extractIdXsecFromUrl } from '@/utils/utils';
import { debounce, get, set } from 'lodash';
import { domesticCodes } from './province_city_codes';
import CookiesUploadModal from './components/cookiesUploadModal';
import { useParams } from 'umi';

const { Content } = Layout;
const { Text } = Typography;
const { Option } = Select;
const provinces = Object.keys(domesticCodes);

const XhsNoteEditor: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const params = useParams<{ id?: string }>(); // 获取URL参数
  const currentUser = initialState?.currentUser;

  // 添加状态来存储note ID
  const [noteId, setNoteId] = useState<number | undefined>(undefined);
  const [cookies, setCookies] = useState<any>(null);
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [isLogging, setIsLogging] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [titleCharCount, setTitleCharCount] = useState<number>(0);
  const [tags, setTags] = useState<string[]>([]);
  const [imgs, setImgs] = useState<string[]>([]);
  const [rewriteTitle, setRewriteTitle] = useState<string[]>([]);
  const [titleRewriteSummary, setTitleRewriteSummary] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [openCaptcha, setOpenCaptcha] = useState<boolean>(true);
  const [qrcode, setQrcode] = useState<string | null>(null);
  const [contentEdit, setContentEdit] = useState<boolean>(false);
  const [isVideoEditorVisible, setIsVideoEditorVisible] =
    useState<boolean>(false);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(
    null,
  );
  const [isTrailerVisible, setIsTrailerVisible] = useState<boolean>(false);
  const [isGoodsVisible, setIsGoodsVisible] = useState<boolean>(false);
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [associatedTrailer, setAssociatedTrailer] = useState<any>(null);
  const [associatedGoods, setAssociatedGoods] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [suggestTags, setSuggestTags] = useState<any[]>([]);
  const [usersOption, setUsersOption] = useState<any[]>([]);
  const [tagsOption, setTagsOption] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [debounceLoading, setDebounceLoading] = useState<boolean>(false);
  const [prefix, setPrefix] = useState<string>('@');
  const [selectedProvince, setSelectedProvince] = useState<string | undefined>(
    undefined,
  );
  const [cities, setCities] = useState<string[]>([]);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(
    undefined,
  );
  const [domesticCode, setDomesticCode] = useState<string | undefined>(
    undefined,
  );
  const [scheduleModalVisible, setScheduleModalVisible] =
    useState<boolean>(false);
  const [scheduledTime, setScheduledTime] = useState<any>(null);
  const urlRef = useRef<any>(null);
  const contentTextAreaRef = useRef<any>(null); // AntD TextArea ref type is tricky, 'any' for simplicity here
  const [notes, setNotes] = useState<any[]>([]); // 添加这行来存储多个笔记
  const [urlInput, setUrlInput] = useState<string>(''); // 添加一个新的状态来控制输入框的值

  // 添加状态来跟踪来源
  const [contentSource, setContentSource] = useState<string>('');
  const [imageSource, setImageSource] = useState<string>('');

  // 添加状态来跟踪选择器的值
  const [selectedContentNote, setSelectedContentNote] = useState<
    string | undefined
  >(undefined);
  const [selectedImageNote, setSelectedImageNote] = useState<
    string | undefined
  >(undefined);

  // 在组件状态中添加选中图片的状态
  const [selectedImages, setSelectedImages] = useState<{
    [key: string]: string[];
  }>({});

  useEffect(() => {
    const options = users.map((user: any) => ({
      key: user.id,
      value: user.name,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', width: '300px' }}>
          <img
            src={user.avatar}
            alt={user.name}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              marginRight: '8px',
            }}
          />
          <span style={{ marginLeft: '10px' }}>{user.name}</span>
        </div>
      ),
    }));
    setUsersOption(options);
  }, [users]);

  useEffect(() => {
    const options = suggestTags.map((tag: any) => ({
      key: tag.id,
      value: tag.name,
      label: (
        <div
          style={{
            width: '300px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ marginLeft: '5px' }}>{`#${tag.name}`}</div>
          <div style={{ marginRight: '5px' }}>{`${tag.view_num}次浏览`}</div>
        </div>
      ),
    }));
    setTagsOption(options);
  }, [suggestTags]);

  useEffect(() => {
    if (!selectedProvince) return;
    const code = domesticCodes[selectedProvince]['code'];
    setDomesticCode(code);
  }, [selectedProvince]);

  useEffect(() => {
    if (!selectedCity || !selectedProvince) return;
    const code =
      domesticCodes[selectedProvince]['cities'][selectedCity]['code'];
    setDomesticCode(code);
  }, [selectedCity]);

  // 监控imgs状态变化，用于调试
  useEffect(() => {
    console.log('imgs state changed:', imgs);
  }, [imgs]);

  const fetchMentionableUsers = async (page: number, query?: string) => {
    const result = (await getAtUser({
      name: currentUser?.name as string,
      page: page,
      keyword: query ?? '',
    })) as any;
    setUsers(result.resp.response);
    setDebounceLoading(false);
  };
  const fetchSuggestTags = async (query?: string) => {
    const result = (await getSuggestTags({
      name: currentUser?.name as string,
      tag: query ?? '',
    })) as any;
    setSuggestTags(result.resp.response);
    setDebounceLoading(false);
  };
  const debounceFetchMentionableUsers = useCallback(
    debounce(fetchMentionableUsers, 300),
    [],
  );
  const debounceFetchSuggestTags = useCallback(
    debounce(fetchSuggestTags, 300),
    [],
  );
  const onMentionUserSearch = (search: string, newPrefix: string) => {
    setPrefix(newPrefix);
    setDebounceLoading(true);
    if (newPrefix === '@') {
      setUsers([]);
      debounceFetchMentionableUsers(1, search);
    } else if (newPrefix === '#') {
      setSuggestTags([]);
      debounceFetchSuggestTags(search);
    }
  };

  const tagStyle = {
    borderRadius: '20px',
    padding: '4px 12px',
    margin: '4px',
    border: '1px solid #d9d9d9',
    background: '#fafafa',
  };

  // 格式化过期时间显示
  const formatExpireTime = (expireTimestamp?: number) => {
    if (!expireTimestamp) return null;

    const now = Date.now() / 1000; // 当前时间的秒级时间戳
    const timeDiff = expireTimestamp - now; // 剩余秒数

    if (timeDiff <= 0) {
      return <Tag color="red">已过期</Tag>;
    }

    const days = Math.floor(timeDiff / (24 * 3600));
    const hours = Math.floor((timeDiff % (24 * 3600)) / 3600);
    const minutes = Math.floor((timeDiff % 3600) / 60);

    let displayText = '';
    if (days > 0) {
      displayText = `${days}天`;
    } else if (hours > 0) {
      displayText = `${hours}小时`;
    } else {
      displayText = `${minutes}分钟`;
    }

    return <Tag color="green">{displayText}后过期</Tag>;
  };

  const handleLogin = () => {
    setIsLogging(true);
    // setLoginPhase('init');
    // clientSocket.socket.emit('xhsLogin', {
    //   name: currentUser?.name ?? 'xiaohongshu',
    // });
    // clientSocket.socket.on('log', (log: string) => {
    //   setLoginLog(log);
    // });
    // clientSocket.socket.on('promptPhone', () => {
    //   setLoginPhase('phone');
    // });
    // clientSocket.socket.on('promptCode', () => {
    //   setLoginPhase('code');
    // });
    // clientSocket.socket.on('qrcode', (qrcode: string) => {
    //   setLoginPhase('qrcode');
    //   setQrcode(qrcode);
    // });
    // clientSocket.socket.on('cookie', (cookies: any) => {
    //   setCookies(cookies);
    // });
    // clientSocket.socket.on('userInfo', (userInfo: any) => {
    //   setAccountInfo(userInfo);
    // });
    // clientSocket.socket.on('error', (errorMessage: string) => {
    //   setIsLogging(false);
    //   alert(errorMessage);
    // });
    // clientSocket.socket.on('login-success', () => {
    //   setIsLogging(false);
    //   setLoginPhase('success');
    // });
  };

  const handleNoteSave = async (
    published: boolean,
    publishedNoteId?: string,
    publishedNoteUrl?: string,
    xsecToken?: string,
  ) => {
    setLoading(true);
    const data = {
      name: currentUser?.name as string,
      title: title,
      content: content,
      imgs,
      tags,
      noteId,
      published,
      publishedNoteId,
      publishedNoteUrl,
      xsecToken,
      // trailer_plan: associatedTrailer,
      // goods: associatedGoods,
      // is_private: isPrivate,
      // area: domesticCode ?? '',
    };

    try {
      const result = (await saveNote(data)) as any;
      setLoading(false);
      if (result.resp.code === 0) {
        console.log('Note saved successfully:', result.resp.data);
        setNoteId(result.resp.data); // 更新noteId
        alert('暂存成功');
      } else {
        alert(`暂存失败: ${result.resp.message || '未知错误'}`);
      }
    } catch (error) {
      setLoading(false);
      alert('暂存失败，请稍后重试');
    }
  };

  const handleNoteCrawl = async (url: string) => {
    if (!url.trim()) {
      alert('请输入有效的链接');
      return;
    }

    setLoading(true);

    try {
      const result = (await getNoteByUrl({
        name: currentUser?.name ?? 'xiaohongshu',
        url: url,
      })) as any;

      if (result.resp.code !== 0) {
        setLoading(false);
        return alert(`获取笔记失败: ${result.resp.message}`);
      }

      const note = result.resp.response;

      // 添加新笔记到列表中，避免重复
      const noteWithUrl = { ...note, url, id: Date.now() };
      setNotes((prevNotes) => {
        const existingIndex = prevNotes.findIndex((n) => n.url === url);
        if (existingIndex >= 0) {
          const newNotes = [...prevNotes];
          newNotes[existingIndex] = noteWithUrl;
          return newNotes;
        } else {
          return [...prevNotes, noteWithUrl];
        }
      });

      // 设置当前编辑的内容（不包括图片）
      setTitle(note.title);
      setContent(note.content);
      setTags(note.tags.map((tag: any) => tag.name));
      // 移除这行：setImgs(note.imgs);

      // 清空输入框
      setUrlInput('');

      setLoading(false);
    } catch (error) {
      setLoading(false);
      alert('获取笔记失败，请稍后重试');
    }
  };

  // 处理图片选择
  const handleImageSelection = (
    noteId: string,
    imageUrl: string,
    checked: boolean,
  ) => {
    setSelectedImages((prev) => {
      const current = prev[noteId] || [];
      if (checked) {
        return {
          ...prev,
          [noteId]: [...current, imageUrl],
        };
      } else {
        return {
          ...prev,
          [noteId]: current.filter((img) => img !== imageUrl),
        };
      }
    });
  };

  // 添加选中的图片到编辑器
  const handleAddSelectedImages = (noteId: string) => {
    const selected = selectedImages[noteId] || [];
    if (selected.length === 0) {
      alert('请先选择要添加的图片');
      return;
    }

    setImgs((prevImgs) => {
      // 去重处理
      const newImgs = [...prevImgs];
      selected.forEach((img) => {
        if (!newImgs.includes(img)) {
          newImgs.push(img);
        }
      });
      return newImgs;
    });

    // 清空当前笔记的选中状态
    setSelectedImages((prev) => ({
      ...prev,
      [noteId]: [],
    }));

    alert(`已添加 ${selected.length} 张图片`);
  };

  const handleNoteContentSelect = (note: any) => {
    // 处理标题 - 如果当前没有标题则使用新标题，有标题则保持不变
    if (!title.trim()) {
      setTitle(note.title);
    }

    // 处理内容 - 追加到现有内容后面
    const newContent = note.content;
    if (content.trim()) {
      // 如果已有内容，空一行后追加新内容
      setContent((prevContent) => prevContent + '\n\n' + newContent);
    } else {
      // 如果没有内容，直接设置新内容
      setContent(newContent);
    }

    // 处理标签 - 合并不重复的标签
    const newTags = note.tags.map((tag: any) => tag.name);
    setTags((prevTags) => {
      const uniqueTags = [...new Set([...prevTags, ...newTags])];
      return uniqueTags;
    });

    // 更新来源信息 - 如果有多个来源，显示"多个来源"
    if (contentSource && contentSource !== note.title) {
      setContentSource('多个来源');
    } else {
      setContentSource(note.title);
    }

    setSelectedContentNote(note.id.toString());
  };

  useEffect(() => {
    const titleLength = calculateUnitCharCount(title);
    setTitleCharCount(titleLength);
  }, [title]);

  const handleNotePublish = async () => {
    setLoading(true);
    const processedTags = tags.map((tag: string) => `#${tag}`);
    if (processedTags && processedTags.length > 10) {
      setLoading(false);
      return alert('标签数量不能超过10个');
    }
    if (titleCharCount > 20) {
      setLoading(false);
      return alert('标题字数不能超过20个');
    }
    const normalizedVideoUrl = generatedVideoUrl?.trim();

    const baseData = {
      name: currentUser?.name as string,
      title: title,
      content: content.replace(/\n\s*\n/g, '\n\t\n'),
      area: domesticCode ?? '',
      tags: processedTags,
      ats: selectedUsers,
      trailer_plan: associatedTrailer,
      goods: associatedGoods,
      is_private: isPrivate,
      noteId: noteId,
    };

    try {
      let result: any;

      if (normalizedVideoUrl) {
        const videoData: Parameters<typeof publishVideoNote>[0] = {
          ...baseData,
          files: [normalizedVideoUrl],
        };
        result = await publishVideoNote(videoData);
      } else {
        const imageData = {
          ...baseData,
          files: imgs,
        };
        result = await publishImageNote(
          imageData as Parameters<typeof publishImageNote>[0],
        );
      }

      if (result?.resp?.code === 0) {
        alert('发布成功');
      } else {
        alert('发布失败');
      }
    } catch (error) {
      console.error('[handleNotePublish] 发布失败:', error);
      alert('发布失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePublish = async () => {
    if (!scheduledTime) {
      alert('请选择发布时间');
      return;
    }

    setLoading(true);
    const processedTags = tags.map((tag: string) => `#${tag}`);
    const data = {
      name: currentUser?.name as string,
      title: title,
      content: content.replace(/\n\s*\n/g, '\n\t\n'),
      files: imgs,
      tags: processedTags,
      ats: selectedUsers,
      trailer_plan: associatedTrailer,
      goods: associatedGoods,
      is_private: isPrivate,
      area: domesticCode ?? '',
      scheduled_time: scheduledTime.format('YYYY-MM-DD HH:mm:ss'),
      noteId: noteId,
    };

    try {
      const result = (await scheduleImageNote(data)) as any;
      setLoading(false);
      if (result.resp.code === 0) {
        alert('定时发布任务创建成功');
        setScheduleModalVisible(false);
        setScheduledTime(null);
      } else {
        alert(`定时发布任务创建失败: ${result.resp.message || '未知错误'}`);
      }
    } catch (error) {
      setLoading(false);
      alert(`定时发布任务创建失败`);
    }
  };

  const handleImgsChange = (newImgs: string[]) => {
    console.log('handleImgsChange called with:', newImgs);
    console.log(
      'Previous imgs length:',
      imgs.length,
      'New imgs length:',
      newImgs.length,
    );

    // 确保使用全新的数组引用，避免状态更新问题
    setImgs([...newImgs]);
  };

  const handleTitleRewrite = async (style: 'mimeng' | 'xhs') => {
    setLoading(true);
    const response: any = await rewriteNoteTitle({ content, style });
    if (!response['resp']['titles']['results'])
      return alert('请求失败,请稍后重试');
    setRewriteTitle(response['resp']['titles']['results']);
    setTitleRewriteSummary(response['resp']['titles']['summary']);
    setLoading(false);
  };

  const handleTrailerPlanSelect = (plan: any) => {
    setAssociatedTrailer(plan);
    setIsTrailerVisible(false);
  };

  const handleGoodsSelect = (goods: any) => {
    setAssociatedGoods(goods);
    setIsGoodsVisible(false);
  };

  const handleMentionSelect = (option: any, prefix: string) => {
    if (prefix === '@') {
      const user = users.find((user: any) => user.id === option.key);
      setSelectedUsers([...selectedUsers, user]);
    }
    if (prefix === '#') {
      const tag = suggestTags.find((tag: any) => tag.id === option.key);
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const fetchAccountInfo = async () => {
    const response = await getXhsAccountInfo({
      name: currentUser?.name ?? 'xiaohongshu',
    });
    if ('data' in response['resp']!) setAccountInfo(response['resp']['data']);
  };

  const clearCookies = async () => {
    const response = await clearXhsCookies({
      name: currentUser?.name ?? 'xiaohongshu',
    });
    if (response['resp']) {
      setCookies(null);
      setAccountInfo(null);
    }
    // fetchAccountInfo();
  };

  useEffect(() => {
    if (currentUser) {
      console.log('User:', currentUser?.name);
      fetchAccountInfo();
    }
  }, [currentUser]); // 依赖数组中包含 user

  // 在useEffect中处理note ID
  useEffect(() => {
    if (params.id) {
      setNoteId(Number(params.id));
      console.log('从URL获取到note ID:', params.id);
      loadNoteById(params.id);
    }
  }, [params.id]);

  // 处理URL参数中的urlInput
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlInputParam = urlParams.get('urlInput');
    if (urlInputParam) {
      console.log('从URL参数获取到urlInput:', urlInputParam);
      setUrlInput(decodeURIComponent(urlInputParam));
    }
  }, []);

  // 根据ID加载笔记的函数
  const loadNoteById = async (id: string) => {
    try {
      setLoading(true);
      const result = (await getNote({
        noteId: Number(id),
        name: currentUser?.name as string,
      })) as any;

      if (result.resp.code === 0) {
        const note = result.resp.data;
        console.log('成功加载笔记:', note);

        // 初始化表单数据
        setTitle(note.title || '');
        setContent(note.content || '');
        setTags(note.tags || []);
        setImgs(note.imageUrls || []);

        // 如果有其他字段，也可以设置
        // setIsPrivate(note.is_private || false);
        // setAssociatedGoods(note.goods || []);
        // setAssociatedTrailer(note.trailer_plan || null);

        console.log('笔记数据已加载到表单');
      } else {
        alert(`加载笔记失败: ${result.resp.message || '未知错误'}`);
        console.error('加载笔记失败:', result.resp.message);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('加载笔记失败:', error);
      alert('加载笔记失败，请稍后重试');
    }
  };

  return (
    <PageMain>
      {/* <CreativeEditorSDK /> */}
      {loading && <FullScreenSpin />}
      {isLogging && (
        // <LoginSms
        //   loginPhase={loginPhase}
        //   qrcode={qrcode}
        //   loginLog={loginLog}
        //   onPhoneClick={handlePhoneClick}
        //   onCodeClick={handleCodeClick}
        // />
        // <LoginModal
        //   visible={isLogging}
        //   name={currentUser?.name}
        //   onCancel={() => setIsLogging(false)}
        //   onLoginSuccess={(data: any) => {
        //     fetchAccountInfo();
        //   }}
        // />
        <CookiesUploadModal
          visible={isLogging}
          name={currentUser?.name ?? 'xiaohongshu'}
          onCancel={() => {
            fetchAccountInfo();
            setIsLogging(false);
          }}
        />
      )}
      <Content
        style={{
          margin: '24px 16px',
          padding: 24,
          background: '#fff',
          minHeight: '360px',
          display: 'flex',
        }}
      >
        <Card
          title="发布图文"
          style={{ flex: 1, borderRadius: '8px' }}
          extra={
            <Space size="large">
              <Dropdown.Button
                style={{
                  background: cookies ? 'gray' : 'red',
                  borderColor: cookies ? 'gray' : 'red',
                  color: 'white',
                  borderRadius: '4px !important',
                }}
                onClick={handleLogin}
                // disabled={cookies !== null}
                menu={{
                  items: [{ key: 1, label: '退出登录' }],
                  onClick: () => clearCookies(),
                }}
                icon={<MoreOutlined />}
              >
                登录
              </Dropdown.Button>
              <Space>
                <div>{accountInfo?.nickname}</div>
                {formatExpireTime(accountInfo?.expire)}
              </Space>
            </Space>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Divider orientation="left" orientationMargin={0}>
              参考笔记
            </Divider>
            <div style={{ width: '100%', display: 'flex' }}>
              <Input
                ref={urlRef}
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                style={{ flex: 1, minWidth: 0, borderRadius: '4px' }}
                placeholder="请输入小红书笔记链接"
                onPressEnter={(e) => {
                  const inputValue = urlInput.trim();
                  if (inputValue) {
                    handleNoteCrawl(inputValue);
                  }
                }}
              />
              <div style={{ width: '30px' }}></div>
              <Button
                type="primary"
                style={{ width: '120px', flexShrink: 0, borderRadius: '4px' }}
                onClick={() => {
                  if (urlInput.trim()) {
                    handleNoteCrawl(urlInput);
                  }
                }}
              >
                获取
              </Button>
            </div>

            {/* 添加笔记badge区域 */}
            {notes.length > 0 && (
              <div style={{ width: '100%', marginTop: '12px' }}>
                <Space wrap size="small">
                  {notes.map((note) => (
                    <Tag
                      key={note.id}
                      color="blue"
                      style={{
                        borderRadius: '4px',
                        padding: '4px 8px',
                        maxWidth: '180px',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {note.title}
                      </span>
                    </Tag>
                  ))}
                </Space>
              </div>
            )}

            <Divider orientation="left" orientationMargin={0}>
              视频生成
            </Divider>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: '12px',
              }}
            >
              <Button
                type="primary"
                onClick={() => setIsVideoEditorVisible(true)}
              >
                智能生成
              </Button>
            </div>
            {generatedVideoUrl && (
              <div
                style={{
                  width: '320px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <video
                  src={generatedVideoUrl}
                  controls
                  style={{
                    width: '60%',
                    borderRadius: '8px',
                    backgroundColor: '#000',
                  }}
                />
              </div>
            )}
            <VideoEditor
              visible={isVideoEditorVisible}
              onCancel={() => setIsVideoEditorVisible(false)}
              onFinish={(url) => {
                setGeneratedVideoUrl(url);
              }}
            />

            <Divider orientation="left" orientationMargin={0}>
              图片编辑
              {imageSource && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    fontWeight: 'normal',
                  }}
                >
                  （来源：{imageSource}）
                </span>
              )}
            </Divider>

            {/* 添加图片选择Tab区域 */}
            {notes.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <Tabs
                  type="card"
                  size="small"
                  items={notes.map((note) => ({
                    key: note.id.toString(),
                    label: (
                      <span
                        style={{
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {note.title}
                      </span>
                    ),
                    children: (
                      <div>
                        <div
                          style={{
                            marginBottom: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            已选择{' '}
                            {selectedImages[note.id.toString()]?.length || 0} /{' '}
                            {note.imgs.length} 张图片
                          </div>
                          <Space>
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedImages((prev) => ({
                                  ...prev,
                                  [note.id.toString()]: note.imgs,
                                }));
                              }}
                            >
                              全选
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedImages((prev) => ({
                                  ...prev,
                                  [note.id.toString()]: [],
                                }));
                              }}
                            >
                              清空
                            </Button>
                            <Button
                              type="primary"
                              size="small"
                              onClick={() =>
                                handleAddSelectedImages(note.id.toString())
                              }
                              disabled={
                                !selectedImages[note.id.toString()]?.length
                              }
                            >
                              添加选中图片
                            </Button>
                          </Space>
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(auto-fill, minmax(120px, 1fr))',
                            gap: '8px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                          }}
                        >
                          {note.imgs.map((img: string, index: number) => (
                            <div
                              key={index}
                              style={{
                                position: 'relative',
                                border: selectedImages[
                                  note.id.toString()
                                ]?.includes(img)
                                  ? '2px solid #1890ff'
                                  : '2px solid transparent',
                                borderRadius: '4px',
                                overflow: 'hidden',
                              }}
                            >
                              <Checkbox
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  zIndex: 1,
                                  borderRadius: '2px',
                                }}
                                checked={
                                  selectedImages[note.id.toString()]?.includes(
                                    img,
                                  ) || false
                                }
                                onChange={(e) =>
                                  handleImageSelection(
                                    note.id.toString(),
                                    img,
                                    e.target.checked,
                                  )
                                }
                              />
                              <img
                                src={img}
                                alt={`图片 ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '120px',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  display: 'block',
                                }}
                                onClick={() => {
                                  const isSelected =
                                    selectedImages[
                                      note.id.toString()
                                    ]?.includes(img) || false;
                                  handleImageSelection(
                                    note.id.toString(),
                                    img,
                                    !isSelected,
                                  );
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </div>
            )}

            <Divider orientation="left" orientationMargin={0}></Divider>

            <ImageWall imgs={imgs} onChange={handleImgsChange} />

            <Space direction="vertical" style={{ width: '100%' }}>
              {contentEdit && (
                <ContentEditor
                  note={content}
                  visible={contentEdit}
                  onCancel={setContentEdit}
                  onFinish={setContent}
                  imgs={imgs}
                />
              )}
              <Divider orientation="left" orientationMargin={0}>
                正文内容
              </Divider>

              {/* 添加正文内容选择Tab区域 */}
              {notes.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <Tabs
                    type="card"
                    size="small"
                    items={notes.map((note) => ({
                      key: note.id.toString(),
                      label: (
                        <span
                          style={{
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {note.title}
                        </span>
                      ),
                      children: (
                        <div>
                          <div
                            style={{
                              marginBottom: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              内容预览（{note.content.length} 字符）
                            </div>
                            <Space>
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleNoteContentSelect(note)}
                              >
                                使用此内容
                              </Button>
                            </Space>
                          </div>
                          <div
                            style={{
                              maxHeight: '150px',
                              overflowY: 'auto',
                              padding: '8px',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              fontSize: '14px',
                              lineHeight: '1.5',
                            }}
                          >
                            {note.content}
                          </div>
                        </div>
                      ),
                    }))}
                  />
                </div>
              )}

              {/* 修改Mentions区域的布局 */}
              <div style={{ position: 'relative', width: '100%' }}>
                <Mentions
                  ref={contentTextAreaRef}
                  loading={debounceLoading}
                  onSearch={onMentionUserSearch}
                  onSelect={handleMentionSelect}
                  prefix={'@'}
                  options={prefix === '@' ? usersOption : tagsOption}
                  autoSize={{ minRows: 6 }}
                  placeholder="输入正文描述,真诚有价值的分享予人温暖"
                  maxLength={1000}
                  style={{
                    width: '100%',
                    borderRadius: '4px',
                    paddingBottom: '40px',
                  }}
                  value={content}
                  onChange={(value: string) => setContent(value)}
                />

                {/* 在输入框右下角添加智能改写按钮和字符计数 */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <span style={{ color: '#999', fontSize: '12px' }}>
                    {`${content.length} / 1000`}
                  </span>
                  <Button
                    size="small"
                    type="primary"
                    style={{ borderRadius: '4px' }}
                    onClick={() => {
                      setContentEdit(true);
                    }}
                  >
                    智能改写
                  </Button>
                </div>
              </div>
              <Space wrap>
                <Tag style={tagStyle}>标签#</Tag>
                {tags.map((tag: any, index: number) => (
                  <Tag
                    key={tag} // 使用标签内容作为稳定的key
                    style={tagStyle}
                    closable={true}
                    onClose={() => {
                      setTags(tags.filter((t) => t !== tag));
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
                <Input
                  placeholder="输入更多"
                  bordered={false}
                  onPressEnter={(e) => {
                    const inputValue = (
                      e.target as HTMLInputElement
                    )?.value?.trim();
                    if (inputValue && !tags.includes(inputValue)) {
                      setTags([...tags, inputValue]);
                      (e.target as HTMLInputElement).value = ''; // 清空输入框
                    }
                  }}
                />
              </Space>

              <Divider orientation="left" orientationMargin={0}>
                笔记标题
              </Divider>
              <Space
                direction="horizontal"
                size={'large'}
                style={{
                  width: '100%',
                }}
              >
                <Button
                  style={{ width: '100px', borderRadius: '4px' }}
                  type="primary"
                  onClick={() => {
                    handleTitleRewrite('mimeng');
                  }}
                >
                  眯蒙标题
                </Button>
                <Button
                  style={{ width: '100px', borderRadius: '4px' }}
                  type="primary"
                  onClick={() => {
                    handleTitleRewrite('xhs');
                  }}
                >
                  小红书标题
                </Button>
              </Space>
              <Input
                placeholder="测试笔记发布"
                style={{ width: '100%', borderRadius: '4px' }}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
                suffix={
                  // 👈 使用 suffix prop
                  <span
                    style={{
                      color: '#999',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {titleCharCount} / 20
                  </span>
                }
              />
              <Radio.Group
                buttonStyle="solid"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  width: '100%',
                }}
              >
                {rewriteTitle.map((item: any, index: number) => (
                  <Tooltip title={item['reason']}>
                    <Radio.Button
                      key={index}
                      value={item['title']}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        setTitleCharCount(
                          calculateUnitCharCount(e.target.value),
                        );
                      }}
                    >
                      {item['title']}
                    </Radio.Button>
                  </Tooltip>
                ))}
              </Radio.Group>
              <div>
                <Text
                  style={{
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '8px',
                  }}
                >
                  {`AI建议：${titleRewriteSummary}`}
                </Text>
              </div>
            </Space>

            <Divider orientation="left" orientationMargin={0}>
              直播&购物车
            </Divider>
            <Space
              id="goods&trailer"
              direction="vertical"
              style={{ width: '100%' }}
              size={'large'}
            >
              <TrailerEditor
                name={currentUser?.name as string}
                visible={isTrailerVisible}
                onCancel={() => setIsTrailerVisible(false)}
                onFinish={handleTrailerPlanSelect}
              />
              <div
                id="trailer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  width: '50%',
                  height: '65px',
                  justifyContent: 'space-between',
                  backgroundColor: 'rgba(0,0,0,0.03)',
                  paddingLeft: '22px',
                  paddingRight: '18px',
                  borderRadius: '6px',
                }}
              >
                <div>
                  {associatedTrailer ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                      }}
                    >
                      <div>{associatedTrailer.title}</div>
                      <div>
                        {new Date(associatedTrailer.start_time)
                          .toLocaleString('zh-CN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })
                          .replace(/\//g, '-')}
                      </div>
                    </div>
                  ) : (
                    '在笔记上关联直播预告'
                  )}
                </div>
                <Space>
                  {associatedTrailer && (
                    <Button
                      type="link"
                      style={{
                        color: '#FF2442',
                      }}
                      onClick={() => setAssociatedTrailer(null)}
                    >
                      取消关联
                    </Button>
                  )}
                  <Button
                    style={{
                      backgroundColor: 'transparent',
                      color: '#FF2442',
                      borderWidth: '1px',
                      borderColor: '#FF2442',
                      borderRadius: '4px',
                    }}
                    onClick={() => setIsTrailerVisible(true)}
                  >
                    关联直播
                  </Button>
                </Space>
              </div>
              <GoodsSelector
                name={currentUser?.name as string}
                visible={isGoodsVisible}
                onCancel={() => setIsGoodsVisible(false)}
                onFinish={handleGoodsSelect}
              />
              {associatedGoods.length === 0 && (
                <div
                  id="goods"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    width: '100%',
                    height: '68px',
                    justifyContent: 'space-between',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    paddingLeft: '22px',
                    paddingRight: '18px',
                    borderRadius: '6px',
                  }}
                >
                  <div>选择店铺内的商品</div>
                  <Button
                    style={{
                      backgroundColor: 'transparent',
                      color: '#FF2442',
                      borderWidth: '1px',
                      borderColor: '#FF2442',
                      borderRadius: '4px',
                    }}
                    onClick={() => setIsGoodsVisible(true)}
                  >
                    添加商品
                  </Button>
                </div>
              )}
              {associatedGoods.length > 0 && (
                <div
                  id="goods"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    flexDirection: 'column',
                    width: '100%',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    paddingLeft: '22px',
                    paddingRight: '18px',
                    borderRadius: '6px',
                  }}
                >
                  <Button
                    type="link"
                    style={{
                      color: '#FF2442',
                      marginBottom: '8px',
                      marginTop: '8px',
                    }}
                    onClick={() => setIsGoodsVisible(true)}
                  >
                    +添加商品
                  </Button>
                  <div
                    style={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: '12px',
                    }}
                  >
                    {associatedGoods.map((product: any) => (
                      <ProductDisplayCard
                        key={product.item_id}
                        product={product}
                        onDelete={(id: string) => {
                          setAssociatedGoods(
                            associatedGoods.filter(
                              (item: any) => item.item_id !== id,
                            ),
                          );
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </Space>

            <Divider orientation="left" orientationMargin={0}>
              更多设置
            </Divider>
            <Space direction="horizontal" size={'large'}>
              <div>添加地点</div>
              <Select
                style={{ width: 160, borderRadius: '4px' }}
                value={selectedProvince}
                onChange={(value) => {
                  setSelectedProvince(value);
                  setCities(
                    Object.keys(
                      (domesticCodes as Record<string, any>)[value]['cities'],
                    ) || [],
                  );
                  setSelectedCity(undefined); // 省份改变时清空已选城市
                }}
              >
                {provinces.map((province) => (
                  <Option key={province} value={province}>
                    {province}
                  </Option>
                ))}
              </Select>
              <Select
                style={{ width: 160, borderRadius: '4px' }}
                placeholder="选择城市"
                value={selectedCity}
                disabled={!selectedProvince || cities.length === 0} // 如果没有选择省份或该省份没有城市数据，则禁用
                onChange={(value) => {
                  setSelectedCity(value);
                }}
              >
                {cities.map((city) => (
                  <Option key={city} value={city}>
                    {city}
                  </Option>
                ))}
              </Select>
            </Space>
            <Space direction="horizontal" size={'large'}>
              <div>可见范围</div>
              <Select
                defaultValue="公开可见"
                options={[
                  { value: '公开可见', label: '公开可见' },
                  { value: '仅自己可见', label: '仅自己可见' },
                ]}
                style={{ width: '240px', borderRadius: '4px' }}
                onSelect={(value) => {
                  value === '公开可见'
                    ? setIsPrivate(false)
                    : setIsPrivate(true);
                }}
              />
            </Space>

            <Space
              style={{ width: '100%', justifyContent: 'flex-end' }}
              direction="horizontal"
            >
              <Button
                style={{
                  borderColor: 'red',
                  color: 'red',
                  borderRadius: '4px',
                  width: '100px',
                }}
                onClick={() => setScheduleModalVisible(true)}
              >
                定时发布
              </Button>
              <Button
                style={{
                  borderColor: '#1890ff',
                  color: '#1890ff',
                  borderRadius: '4px',
                  width: '100px',
                }}
                onClick={() => handleNoteSave(false)}
              >
                暂存
              </Button>
              <Button
                type="primary"
                style={{
                  background: 'red',
                  borderColor: 'red',
                  borderRadius: '4px',
                  width: '100px',
                }}
                onClick={() => handleNotePublish()}
              >
                发布
              </Button>
            </Space>
          </Space>
        </Card>

        {/* 定时发布模态框 */}
        <Modal
          title="定时发布"
          open={scheduleModalVisible}
          onOk={handleSchedulePublish}
          onCancel={() => {
            setScheduleModalVisible(false);
            setScheduledTime(null);
          }}
          okText="确认定时发布"
          cancelText="取消"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>请选择发布时间：</div>
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择发布时间"
              style={{ width: '100%' }}
              value={scheduledTime}
              onChange={(value) => setScheduledTime(value)}
              disabledDate={(current) => {
                // 禁用今天之前的日期
                return current && current.isBefore(new Date(), 'day');
              }}
              disabledTime={(current) => {
                const now = new Date();
                if (current && current.isSame(now, 'day')) {
                  return {
                    disabledHours: () => {
                      const hours = [];
                      for (let i = 0; i < now.getHours(); i++) {
                        hours.push(i);
                      }
                      return hours;
                    },
                    disabledMinutes: (selectedHour) => {
                      if (selectedHour === now.getHours()) {
                        const minutes = [];
                        for (let i = 0; i <= now.getMinutes(); i++) {
                          minutes.push(i);
                        }
                        return minutes;
                      }
                      return [];
                    },
                  };
                }
                return {};
              }}
            />
          </Space>
        </Modal>

        {/* <Preview title={title} content={content}></Preview> */}
      </Content>
    </PageMain>
  );
};

export default XhsNoteEditor;
